import {
  CaretDownOutlined,
  CaretRightOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { ReactComponent as ExportOutlined } from '../../../../../features/beta/modules/Dashboard/AutomationsDashboard/open-link-icon.svg';
import { Button, Input } from '@mm/components';
import { useContactsCRUDActions } from '@mm/state/actions/ContactsCRUDActions';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { Alert, Checkbox } from 'antd';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import validator from 'validator';
import styles from './EmailTemplateUrlsAdder.module.css';
import { useEmailWidgetHandlers } from '../../../../../../common/helpers/widget-helpers/use-email-widget-handlers';
import { uniq } from 'lodash';

export const EmailTemplateUrlsAdder = ({
  selectedLandings,
  onChangeSelectedLandings,
  stateAndHandlers,
}: {
  selectedLandings: string[];
  onChangeSelectedLandings: (url: string) => void;
  stateAndHandlers: ReturnType<typeof useEmailWidgetHandlers>;
}) => {
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const defaultLandings = selectedLandings.filter(url => url !== 'master');
  const [landings, setLandings] = useState<string[]>([...defaultLandings]);
  const [newLandingUrl, setNewLandingUrl] = useState('');
  const [error, setError] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);

  const { setConfigEntities } = stateAndHandlers || {};

  const reachedTemplatesLimit = selectedLandings.length >= 20;

  const { getContactsMostVisistedUrls } = useContactsCRUDActions();

  const getLandingPages = async () => {
    const landings = await getContactsMostVisistedUrls(facebookPageId);
    setLandings(prevState => uniq([...(landings || []), ...prevState]));
  };

  const onAddNewLandingUrl = () => {
    if (validator.isURL(newLandingUrl)) {
      if (!landings.includes(newLandingUrl)) {
        setLandings(prevState => [...prevState, newLandingUrl]);
        setNewLandingUrl('');
        setError('');
        return;
      }
      setError('Landing page already exists');
    } else {
      setError('Not a valid URL');
    }
  };

  const onSelectCheckbox = (url: string) => {
    onChangeSelectedLandings(url);
    setConfigEntities(prevState => [
      ...prevState,
      {
        url,
        master: false,
        cc: '',
        bcc: '',
        subject: '',
        body: '',
        previewText: '',
      },
    ]);
  };

  useEffect(() => {
    getLandingPages();
  }, [facebookPageId]);

  return (
    <div
      className={classNames(styles.container, {
        [styles.expandedContainer]: isExpanded,
      })}
    >
      <div className={styles.header} onClick={() => setIsExpanded(!isExpanded)}>
        {isExpanded ? (
          <CaretDownOutlined className={styles.arrow} />
        ) : (
          <CaretRightOutlined className={styles.arrow} />
        )}
        Add landing page URLs
      </div>
      {isExpanded && (
        <>
          {!reachedTemplatesLimit ? (
            <div className={styles.addUrlBlock}>
              Add URL
              <div className={styles.inputWrapper}>
                <Input
                  value={newLandingUrl}
                  onChange={e => setNewLandingUrl(e.target.value)}
                  placeholder="Landing page link"
                  css={{
                    container: styles.inputContainer,
                    input: styles.input,
                  }}
                  invalid={!!error}
                />
                <Button
                  onClick={onAddNewLandingUrl}
                  css={{ container: styles.button }}
                >
                  Add landing page
                </Button>
              </div>
              <div className={styles.error}>{error}</div>
            </div>
          ) : (
            <Alert
              className={styles.alert}
              message={
                <div style={{ fontWeight: 500 }} className={styles.alertText}>
                  You reached 20 landing page URLs in this email widget.
                </div>
              }
              description={
                <div className={styles.alertText}>
                  We are working on expanding the amount.
                </div>
              }
              type="warning"
              icon={
                <ExclamationCircleOutlined className={styles.exclamationIcon} />
              }
              showIcon
            />
          )}
          {!!landings.length && (
            <div className={styles.listContainer}>
              <span style={{ fontWeight: 500 }}>My Top 20 URLs</span>
              {landings.map((url, index) => (
                <div className={styles.landingRow} key={`${url}_${index}`}>
                  <Checkbox
                    className={styles.checkbox}
                    checked={selectedLandings.includes(url)}
                    onChange={() => onSelectCheckbox(url)}
                    disabled={
                      reachedTemplatesLimit && !selectedLandings.includes(url)
                    }
                  >
                    {`${index + 1}. ${url}`}
                  </Checkbox>
                  <ExportOutlined
                    className={styles.openLinkIcon}
                    onClick={() => window.open(url)}
                  />
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};
